import type { SurveyAnswer } from "../session/transport";
import type { Question } from "../util/types";
import { conjointModule } from "./conjoint/module";
import type { AnyQuestionModule, QuestionModule } from "./contracts";
import { embedModule } from "./embed/module";
import { gridMultiSelectModule } from "./gridMultiSelect/module";
import { gridNumericModule } from "./gridNumeric/module";
import { hasValidGridQuestionGroupMetadata } from "./gridQuestionGroup/core";
import { gridQuestionGroupModule } from "./gridQuestionGroup/module";
import { gridRatingModule } from "./gridRating/module";
import { gridSelectModule } from "./gridSelect/module";
import { gridThisOrThatModule } from "./gridThisOrThat/module";
import { gridThisOrThatRatingModule } from "./gridThisOrThatRating/module";
import { imageModule } from "./image/module";
import { maxDiffModule } from "./maxDiff/module";
import { multiSelectModule } from "./multiSelect/module";
import { numericModule } from "./numeric/module";
import { phoneModule } from "./phone/module";
import { hasValidQuestionGroupMetadata } from "./questionGroup/core";
import { questionGroupModule } from "./questionGroup/module";
import { rankSortModule } from "./rankSort/module";
import { ratingModule } from "./rating/module";
import { selectModule } from "./select/module";
import { textModule } from "./text/module";
import { textHighlighterModule } from "./textHighlighter/module";
import { textListModule } from "./textList/module";
import { videoModule } from "./video/module";

export class QuestionRegistry {
  readonly #modules = new Map<string, AnyQuestionModule>();

  register<Raw, Definition, Draft, Event, Response extends SurveyAnswer>(
    module: QuestionModule<Raw, Definition, Draft, Event, Response>,
  ): void {
    if (this.#modules.has(module.type)) {
      throw new Error(`Question module already registered: ${module.type}`);
    }
    if (!module.views[module.defaultDisplay.view]) {
      throw new Error(
        `Default view '${module.defaultDisplay.view}' is not registered for ${module.type}`,
      );
    }
    this.#modules.set(module.type, module as unknown as AnyQuestionModule);
  }

  get(type: string): AnyQuestionModule | undefined {
    return this.#modules.get(type);
  }

  list(): readonly AnyQuestionModule[] {
    return [...this.#modules.values()];
  }
}

export const questionRegistry = new QuestionRegistry();
export const supportsSurveyQuestion = (question: Question): boolean => {
  const raw = question.question;
  if (raw.type === "complete" || raw.type === "webview_modal") return true;
  if (raw.type === "question_group") {
    if (!hasValidQuestionGroupMetadata(raw)) return false;
    if (
      raw.row_name !== undefined ||
      raw.children.some((child) => child.row !== undefined)
    ) {
      return false;
    }
    return raw.children.every(
      (child) =>
        typeof child?.question?.type === "string" &&
        child.question.type !== "question_group" &&
        child.question.type !== "video" &&
        questionRegistry.get(child.question.type) !== undefined,
    );
  }
  if (raw.type === "grid_question_group") {
    if (!hasValidGridQuestionGroupMetadata(raw)) return false;
    return raw.children.every(
      (child) =>
        ["text", "numeric", "select", "multi-select", "image"].includes(
          child.question.type,
        ) && questionRegistry.get(child.question.type) !== undefined,
    );
  }
  return questionRegistry.get(raw.type) !== undefined;
};
questionRegistry.register(selectModule);
questionRegistry.register(textListModule);
questionRegistry.register(textModule);
questionRegistry.register(numericModule);
questionRegistry.register(phoneModule);
questionRegistry.register(multiSelectModule);
questionRegistry.register(ratingModule);
questionRegistry.register(gridSelectModule);
questionRegistry.register(gridMultiSelectModule);
questionRegistry.register(gridRatingModule);
questionRegistry.register(gridNumericModule);
questionRegistry.register(rankSortModule);
questionRegistry.register(maxDiffModule);
questionRegistry.register(gridThisOrThatModule);
questionRegistry.register(gridThisOrThatRatingModule);
questionRegistry.register(conjointModule);
questionRegistry.register(textHighlighterModule);
questionRegistry.register(imageModule);
questionRegistry.register(videoModule);
questionRegistry.register(embedModule);
questionRegistry.register(questionGroupModule);
questionRegistry.register(gridQuestionGroupModule);
